import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Col, FormGroup, FormLabel, Row, Spinner } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link, useSearchParams } from 'react-router-dom';
import InputText from '../../../components/Forms/Element/InputText';
import CnpjInput from '../../../components/Common/CnpjInput';
import AsteriskRequired from '../../../components/Common/AsteriskRequired';
import AddressRegistration from '../../../components/Custom/Common/Address/AddressRegistration';
import PublicBrandSignature from '../../../components/Common/PublicBrandSignature';
import TurnstileWidget, { TURNSTILE_SITE_KEY } from '../../../components/Common/TurnstileWidget';
import { getSignupPlan, getSignupPlans, lookupSignupCnpj, submitSignup } from '../../../../services/SaaSSignupService';
import { fillFormFromCnpjLookup, normalizeCnpjDigits } from '../../../../utils/CnpjLookupUtils';
import { formatNumberWithTwoDecimals } from '../../../../utils/FormatUtils';
import { PLATFORM_SITE_URL, PROJECT_NAME } from '../../../../config/branding';
import colmeiaSymbol from '../../../../images/logo.png';
import colmeiaWordmark from '../../../../images/colmeia.svg';
import './SaaSSignup.css';

// Sufixo do preço conforme o ciclo de cobrança do plano.
const CYCLE_SUFFIX = { MONTHLY: '/mês', QUARTERLY: '/trimestre', YEARLY: '/ano' };

// Mensagem padrão quando a API não devolve uma explicação legível.
const GENERIC_ERROR = 'Não foi possível concluir o cadastro agora. Tente novamente em instantes.';

/** Preço do plano no formato da vitrine: "R$ 99,90/mês". */
const formatPlanPrice = (plan) =>
    `R$ ${formatNumberWithTwoDecimals(Number(plan.price))}${CYCLE_SUFFIX[plan.cycle] || ''}`;

/** Limites do plano em linguagem de vitrine; limite vazio é ilimitado. */
const describePlanLimits = (plan) => {
    const branches = plan.maxBranches
        ? `${plan.maxBranches === 1 ? '1 filial' : `Até ${plan.maxBranches} filiais`}`
        : 'Filiais ilimitadas';
    const professionals = plan.maxProfessionals
        ? `Até ${plan.maxProfessionals} colaboradores`
        : 'Colaboradores ilimitados';
    return [branches, professionals];
};

/** Data da API (AAAA-MM-DD) no formato brasileiro, sem passar por fuso horário. */
const formatDueDate = (isoDate) => {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}/${month}/${year}`;
};

/**
 * Contratação do sistema pelo próprio cliente, a partir do site.
 *
 * O site leva para cá com ?plan={uuid}; um plano público vem marcado e pode ser trocado.
 * O link exclusivo mostra apenas o plano negociado, para não misturá-lo com a vitrine. Ao
 * concluir, a empresa já existe, o administrador recebe o e-mail de primeiro acesso e a
 * primeira fatura vence no mesmo dia.
 */
const SaaSSignupPage = () => {
    const [searchParams] = useSearchParams();
    const requestedPlanUuid = searchParams.get('plan');
    const [plans, setPlans] = useState([]);
    const [loadingPlans, setLoadingPlans] = useState(true);
    const [plansError, setPlansError] = useState('');
    const [requestedPlanUnavailable, setRequestedPlanUnavailable] = useState(false);
    const [isLookingUpCnpj, setIsLookingUpCnpj] = useState(false);
    const [cnpjNotice, setCnpjNotice] = useState('');
    const [turnstileToken, setTurnstileToken] = useState('');
    const [turnstileFailed, setTurnstileFailed] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [result, setResult] = useState(null);
    const turnstileRef = useRef(null);
    const cnpjLookupRequestRef = useRef(0);
    const lastLookedUpCnpjRef = useRef('');
    const currentCnpjRef = useRef('');

    const formik = useFormik({
        initialValues: {
            planUuid: '',
            cnpj: '',
            name: '',
            corporateName: '',
            phone: '',
            responsibleName: '',
            firstBranchName: 'Matriz',
            zipCode: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
            adminFirstName: '',
            adminLastName: '',
            adminEmail: '',
            acceptedTerms: false,
        },
        validationSchema: Yup.object().shape({
            planUuid: Yup.string().required('Escolha um plano.'),
            cnpj: Yup.string()
                .required('Informe o CNPJ.')
                .test('cnpj-length', 'Informe o CNPJ completo.', (value) => normalizeCnpjDigits(value).length === 14),
            name: Yup.string().trim().min(3, 'Use pelo menos 3 caracteres.').required('Informe o nome fantasia.'),
            corporateName: Yup.string().trim().min(3, 'Use pelo menos 3 caracteres.').required('Informe a razão social.'),
            phone: Yup.string()
                .required('Informe um telefone para contato.')
                .test('phone-length', 'Informe o telefone com DDD.', (value) => (value || '').replace(/\D/g, '').length >= 10),
            responsibleName: Yup.string().max(100, 'Máximo de 100 caracteres.'),
            firstBranchName: Yup.string().trim().min(3, 'Use pelo menos 3 caracteres.').required('Informe o nome da unidade.'),
            adminFirstName: Yup.string().trim().min(2, 'Use pelo menos 2 caracteres.').required('Informe seu nome.'),
            adminLastName: Yup.string().trim().min(2, 'Use pelo menos 2 caracteres.').required('Informe seu sobrenome.'),
            adminEmail: Yup.string().trim().email('Informe um e-mail válido.').required('Informe seu e-mail.'),
            acceptedTerms: Yup.boolean().oneOf([true], 'Aceite os termos para continuar.'),
        }),
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitError('');
            if (!turnstileToken) {
                setSubmitError('Confirme a verificação de segurança antes de enviar.');
                setSubmitting(false);
                return;
            }

            try {
                const response = await submitSignup({
                    planUuid: values.planUuid,
                    cnpj: values.cnpj.trim(),
                    name: values.name.trim(),
                    corporateName: values.corporateName.trim(),
                    phone: values.phone.trim(),
                    responsibleName: values.responsibleName.trim(),
                    adminFirstName: values.adminFirstName.trim(),
                    adminLastName: values.adminLastName.trim(),
                    adminEmail: values.adminEmail.trim(),
                    firstBranch: {
                        name: values.firstBranchName.trim(),
                        address: {
                            zipCode: values.zipCode.trim(),
                            street: values.street.trim(),
                            number: values.number.trim(),
                            complement: values.complement.trim(),
                            neighborhood: values.neighborhood.trim(),
                            city: values.city.trim(),
                            state: values.state.trim(),
                        },
                    },
                    acceptedTerms: values.acceptedTerms,
                    turnstileToken,
                });
                setResult(response.data);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } catch (error) {
                setSubmitError(error?.response?.data?.message || GENERIC_ERROR);
                // O token do captcha é de uso único: depois de uma recusa, o visitante resolve de novo.
                turnstileRef.current?.reset();
            } finally {
                setSubmitting(false);
            }
        },
    });
    const currentFormValuesRef = useRef(formik.values);
    currentFormValuesRef.current = formik.values;

    useEffect(() => {
        document.title = `Contratar | ${PROJECT_NAME}`;
        let active = true;
        setLoadingPlans(true);
        setPlansError('');
        setRequestedPlanUnavailable(false);
        // A lista pública nunca contém planos exclusivos. Consulta o UUID do link em separado
        // e, se for exclusivo, restringe a escolha a ele. Um 404 permite oferecer os planos do
        // site, mas outra falha da consulta não deve ser confundida com link revogado.
        Promise.allSettled([getSignupPlans(), requestedPlanUuid ? getSignupPlan(requestedPlanUuid) : Promise.resolve(null)])
            .then(([listResult, linkedResult]) => {
                if (!active) return;
                const linked = linkedResult.status === 'fulfilled' ? linkedResult.value?.data : null;
                if (listResult.status === 'rejected' && !linked?.exclusive) {
                    throw listResult.reason;
                }
                const available = listResult.status === 'fulfilled' ? listResult.value.data || [] : [];
                if (linkedResult.status === 'rejected'
                    && linkedResult.reason?.response?.status !== 404
                    && !available.some((plan) => plan.uuid === requestedPlanUuid)) {
                    throw linkedResult.reason;
                }
                const displayed = linked?.exclusive ? [linked] : available;
                const requested = displayed.find((plan) => plan.uuid === requestedPlanUuid);
                const initial = requested || (displayed.length === 1 ? displayed[0] : null);
                setPlans(displayed);
                setRequestedPlanUnavailable(Boolean(requestedPlanUuid && !requested));
                formik.setFieldValue('planUuid', initial?.uuid || '');
            })
            .catch(() => {
                if (active) setPlansError('Não foi possível carregar os planos. Atualize a página para tentar de novo.');
            })
            .finally(() => {
                if (active) setLoadingPlans(false);
            });
        return () => { active = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestedPlanUuid]);

    // Envio recusado pela validação: leva a pessoa ao primeiro erro. No celular o botão fica no
    // fim da página e, sem isso, os avisos dos campos lá em cima passam despercebidos.
    // A validação do Yup é assíncrona: espera o envio terminar para os erros já estarem na tela.
    useEffect(() => {
        if (formik.isSubmitting || formik.submitCount === 0 || Object.keys(formik.errors).length === 0) {
            return;
        }
        const firstError = document.querySelector('.saas-signup-page .invalid-feedback, .saas-signup-page .text-danger');
        firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.isSubmitting]);

    const selectedPlan = useMemo(
        () => plans.find((plan) => plan.uuid === formik.values.planUuid) || null,
        [plans, formik.values.planUuid],
    );

    const handleCnpjChange = (event) => {
        const normalizedCnpj = normalizeCnpjDigits(event.target.value);
        if (normalizedCnpj !== currentCnpjRef.current) {
            // Uma resposta anterior não pode preencher a empresa de um CNPJ já alterado.
            cnpjLookupRequestRef.current += 1;
            currentCnpjRef.current = normalizedCnpj;
            lastLookedUpCnpjRef.current = '';
            setIsLookingUpCnpj(false);
            setCnpjNotice('');
        }
        formik.handleChange(event);
    };

    const handleLookupCnpj = async (cnpj) => {
        const normalizedCnpj = normalizeCnpjDigits(cnpj);
        if (normalizedCnpj.length !== 14 || normalizedCnpj === lastLookedUpCnpjRef.current) return;

        const requestId = ++cnpjLookupRequestRef.current;
        const valuesAtRequest = { ...formik.values };
        setIsLookingUpCnpj(true);
        try {
            const response = await lookupSignupCnpj(normalizedCnpj);
            if (requestId !== cnpjLookupRequestRef.current) return;
            // Se a pessoa já começou a corrigir outro campo, a consulta não sobrescreve a edição.
            const statusWarning = fillFormFromCnpjLookup(
                formik,
                response.data || {},
                normalizedCnpj,
                (field) => currentFormValuesRef.current[field] === valuesAtRequest[field],
            );
            lastLookedUpCnpjRef.current = normalizedCnpj;
            setCnpjNotice(statusWarning || 'Dados preenchidos a partir do CNPJ. Confira antes de enviar.');
        } catch (error) {
            if (requestId === cnpjLookupRequestRef.current) {
                setCnpjNotice(error?.response?.data?.message || 'Não encontramos os dados deste CNPJ. Preencha manualmente.');
            }
        } finally {
            if (requestId === cnpjLookupRequestRef.current) setIsLookingUpCnpj(false);
        }
    };

    const handleCnpjBlur = (event) => {
        formik.handleBlur(event);
        handleLookupCnpj(event.target.value);
    };

    // Mostra o erro do campo só depois de a pessoa passar por ele ou tentar enviar.
    const fieldError = (name) => (formik.touched[name] || formik.submitCount > 0 ? formik.errors[name] : '');

    if (result) {
        return (
            <main className="saas-signup-page">
                <section className="saas-signup-shell saas-signup-shell--narrow">
                    <div className="saas-signup-card saas-signup-done">
                        <div className="saas-signup-done__icon"><i className="fa-solid fa-circle-check"></i></div>
                        <h1>Tudo pronto!</h1>
                        <p>
                            Enviamos o link de primeiro acesso para <strong>{result.adminEmail}</strong>.
                            Abra o e-mail e crie sua senha para entrar no sistema.
                        </p>

                        <div className="saas-signup-done__summary">
                            <div>
                                <span>Plano</span>
                                <strong>{result.planName}</strong>
                            </div>
                            <div>
                                <span>Primeira fatura</span>
                                <strong>
                                    {formatPlanPrice({ price: result.planPrice, cycle: result.planCycle })}
                                    {result.firstDueDate ? ` · vence em ${formatDueDate(result.firstDueDate)}` : ''}
                                </strong>
                            </div>
                        </div>

                        {result.paymentLink ? (
                            <Button
                                className="saas-signup-button"
                                href={result.paymentLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <i className="fa-solid fa-receipt me-2"></i>Pagar primeira fatura
                            </Button>
                        ) : (
                            <p className="saas-signup-hint">
                                A fatura também chega por e-mail. Você pode pagá-la pelo sistema assim que entrar.
                            </p>
                        )}
                        <Link className="btn btn-outline-primary saas-signup-button" to="/login">
                            Ir para o login
                        </Link>

                        <p className="saas-signup-hint">
                            O link do e-mail vale por 30 minutos. Se expirar ou o e-mail não chegar,
                            use <strong>Esqueci minha senha</strong> na tela de login com o mesmo e-mail.
                        </p>
                    </div>
                    <PublicBrandSignature className="is-compact" />
                </section>
            </main>
        );
    }

    return (
        <main className="saas-signup-page">
            <section className="saas-signup-shell">
                <header className="saas-signup-header">
                    {/* Usa o mesmo par símbolo e nome mostrado nas telas de acesso. */}
                    <a href={PLATFORM_SITE_URL} className="saas-signup-logo" aria-label={`Voltar ao site do ${PROJECT_NAME}`}>
                        <img className="saas-signup-logo__symbol" src={colmeiaSymbol} alt="" />
                        <img className="saas-signup-logo__wordmark" src={colmeiaWordmark} alt={PROJECT_NAME} />
                    </a>
                    <h1>Contrate em poucos minutos</h1>
                    <p>Preencha os dados abaixo e comece a usar hoje mesmo. Sem taxa de adesão.</p>
                </header>

                <form onSubmit={formik.handleSubmit} noValidate>
                    <div className="saas-signup-card">
                        <div className="saas-signup-section-title">
                            <span>1</span>
                            <h2>Escolha o plano</h2>
                        </div>

                        {loadingPlans && (
                            <div className="saas-signup-state"><Spinner animation="border" size="sm" /> Carregando planos…</div>
                        )}
                        {plansError && <div className="saas-signup-alert saas-signup-alert--error">{plansError}</div>}
                        {!loadingPlans && !plansError && plans.length === 0 && !requestedPlanUnavailable && (
                            <div className="saas-signup-alert">
                                No momento a contratação pelo site está indisponível.
                                Fale com a nossa equipe pelo <a href={`${PLATFORM_SITE_URL}/#contato`}>site</a>.
                            </div>
                        )}
                        {requestedPlanUnavailable && (
                            <div className="saas-signup-alert">
                                O plano do link não está mais disponível. Escolha uma das opções abaixo.
                            </div>
                        )}

                        <div className="saas-signup-plans" role="radiogroup" aria-label="Planos">
                            {plans.map((plan) => {
                                const selected = plan.uuid === formik.values.planUuid;
                                return (
                                    <button
                                        key={plan.uuid}
                                        type="button"
                                        role="radio"
                                        aria-checked={selected}
                                        className={`saas-signup-plan ${selected ? 'is-selected' : ''}`}
                                        onClick={() => formik.setFieldValue('planUuid', plan.uuid)}
                                    >
                                        <span className="saas-signup-plan__check"><i className="fa-solid fa-check"></i></span>
                                        <strong className="saas-signup-plan__name">{plan.name}</strong>
                                        {plan.description && <span className="saas-signup-plan__description">{plan.description}</span>}
                                        <span className="saas-signup-plan__price">{formatPlanPrice(plan)}</span>
                                        <ul>
                                            {describePlanLimits(plan).map((limit) => <li key={limit}>{limit}</li>)}
                                        </ul>
                                    </button>
                                );
                            })}
                        </div>
                        {fieldError('planUuid') && <div className="text-danger mt-2">{fieldError('planUuid')}</div>}
                    </div>

                    <div className="saas-signup-card">
                        <div className="saas-signup-section-title">
                            <span>2</span>
                            <h2>Dados da empresa</h2>
                        </div>
                        <Row className="g-3">
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>CNPJ <AsteriskRequired /></FormLabel>
                                    <CnpjInput
                                        name="cnpj"
                                        inputMode="numeric"
                                        placeholder="00.000.000/0000-00"
                                        onChange={handleCnpjChange}
                                        onBlur={handleCnpjBlur}
                                        value={formik.values.cnpj}
                                        error={fieldError('cnpj')}
                                    />
                                    <small className="text-muted d-block mt-1" role="status">
                                        {isLookingUpCnpj ? 'Consultando dados do CNPJ…' : cnpjNotice || 'Ao sair do campo, tentaremos preencher os dados automaticamente.'}
                                    </small>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>Nome fantasia <AsteriskRequired /></FormLabel>
                                    <InputText
                                        name="name"
                                        placeholder="Como seus clientes conhecem a empresa"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.name}
                                        error={fieldError('name')}
                                        maxLength="50"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>Razão social <AsteriskRequired /></FormLabel>
                                    <InputText
                                        name="corporateName"
                                        placeholder="Nome registrado no CNPJ"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.corporateName}
                                        error={fieldError('corporateName')}
                                        maxLength="50"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>Telefone <AsteriskRequired /></FormLabel>
                                    <InputText
                                        name="phone"
                                        type="tel"
                                        inputMode="tel"
                                        placeholder="(11) 99999-9999"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.phone}
                                        error={fieldError('phone')}
                                        maxLength="30"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>Responsável</FormLabel>
                                    <InputText
                                        name="responsibleName"
                                        placeholder="Quem responde pela empresa"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.responsibleName}
                                        error={fieldError('responsibleName')}
                                        maxLength="100"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>

                    <div className="saas-signup-card">
                        <div className="saas-signup-section-title">
                            <span>3</span>
                            <h2>Primeira unidade</h2>
                        </div>
                        <p className="saas-signup-section-hint">
                            É a unidade onde os atendimentos acontecem. Outras podem ser cadastradas depois, conforme o plano.
                        </p>
                        <Row className="g-3 mb-3">
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>Nome da unidade <AsteriskRequired /></FormLabel>
                                    <InputText
                                        name="firstBranchName"
                                        placeholder="Ex.: Matriz"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.firstBranchName}
                                        error={fieldError('firstBranchName')}
                                        maxLength="50"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <AddressRegistration formik={formik} />
                    </div>

                    <div className="saas-signup-card">
                        <div className="saas-signup-section-title">
                            <span>4</span>
                            <h2>Seu acesso</h2>
                        </div>
                        <p className="saas-signup-section-hint">
                            Você será o administrador do sistema. O link para criar a senha chega neste e-mail.
                        </p>
                        <Row className="g-3">
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>Nome <AsteriskRequired /></FormLabel>
                                    <InputText
                                        name="adminFirstName"
                                        autoComplete="given-name"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.adminFirstName}
                                        error={fieldError('adminFirstName')}
                                        maxLength="20"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <FormLabel>Sobrenome <AsteriskRequired /></FormLabel>
                                    <InputText
                                        name="adminLastName"
                                        autoComplete="family-name"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.adminLastName}
                                        error={fieldError('adminLastName')}
                                        maxLength="20"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={12}>
                                <FormGroup>
                                    <FormLabel>E-mail <AsteriskRequired /></FormLabel>
                                    <InputText
                                        name="adminEmail"
                                        type="email"
                                        inputMode="email"
                                        autoComplete="email"
                                        placeholder="voce@empresa.com"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.adminEmail}
                                        error={fieldError('adminEmail')}
                                        maxLength="50"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>

                    <div className="saas-signup-card saas-signup-checkout">
                        {selectedPlan && (
                            <div className="saas-signup-checkout__summary">
                                <div>
                                    <span>Plano escolhido</span>
                                    <strong>{selectedPlan.name}</strong>
                                </div>
                                <strong className="saas-signup-checkout__price">{formatPlanPrice(selectedPlan)}</strong>
                            </div>
                        )}
                        <ul className="saas-signup-checkout__facts">
                            <li><i className="fa-solid fa-bolt"></i>Acesso liberado assim que o cadastro termina.</li>
                            <li><i className="fa-solid fa-receipt"></i>A primeira fatura vence hoje; o link de pagamento aparece ao concluir.</li>
                            <li><i className="fa-solid fa-rotate"></i>Cobrança recorrente, sem taxa de adesão.</li>
                        </ul>

                        <label className="saas-signup-terms">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                name="acceptedTerms"
                                checked={formik.values.acceptedTerms}
                                onChange={formik.handleChange}
                            />
                            <span>
                                Li e aceito os{' '}
                                <a href={`${PLATFORM_SITE_URL}/termos.html`} target="_blank" rel="noopener noreferrer">Termos de uso</a>
                                {' '}e a{' '}
                                <a href={`${PLATFORM_SITE_URL}/privacidade.html`} target="_blank" rel="noopener noreferrer">Política de privacidade</a>.
                            </span>
                        </label>
                        {fieldError('acceptedTerms') && <div className="text-danger mb-2">{fieldError('acceptedTerms')}</div>}

                        {TURNSTILE_SITE_KEY && !turnstileFailed ? (
                            <TurnstileWidget
                                ref={turnstileRef}
                                onToken={setTurnstileToken}
                                onLoadError={() => setTurnstileFailed(true)}
                            />
                        ) : !TURNSTILE_SITE_KEY ? (
                            <div className="saas-signup-alert saas-signup-alert--error" role="alert">
                                A verificação de segurança está indisponível no momento. Entre em contato com a nossa equipe.
                            </div>
                        ) : (
                            <div className="saas-signup-alert saas-signup-alert--error">
                                Não foi possível carregar a verificação de segurança. Desative bloqueadores
                                de conteúdo para este site e atualize a página.
                            </div>
                        )}

                        {submitError && (
                            <div className="saas-signup-alert saas-signup-alert--error" role="alert">
                                <i className="fa-solid fa-circle-exclamation me-2"></i>{submitError}
                            </div>
                        )}

                        <Button
                            type="submit"
                            className="saas-signup-button"
                            disabled={formik.isSubmitting || plans.length === 0}
                        >
                            {formik.isSubmitting ? (
                                <><Spinner animation="border" size="sm" className="me-2" />Criando sua conta…</>
                            ) : (
                                <>Contratar {selectedPlan ? selectedPlan.name : ''}</>
                            )}
                        </Button>
                        <p className="saas-signup-hint">
                            Já tem conta? <Link to="/login">Entrar</Link>
                        </p>
                    </div>
                </form>

                <PublicBrandSignature className="is-compact" />
            </section>
        </main>
    );
};

export default SaaSSignupPage;
